import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArtDetail } from '../../services/marketplaceService';
import { resolveImageUrl, onImageError } from '../../utils/imageUrl';
import '../../styles/MarketplacePage.css';

export default function ArtworkDetail() {
  const { artId } = useParams();
  const [art, setArt] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetch = async () => {
      try {
        const res = await getArtDetail(artId);
        if (!cancelled) {
          setArt(res?.data?.data?.art || null);
          setShop(res?.data?.data?.shop || null);
        }
      } catch {
        if (!cancelled) setError('Artwork not found on the marketplace.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [artId]);

  const formatPrice = (p) =>
    p != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p) : '';

  if (loading) return <div style={{ textAlign: 'center', padding: '4rem', color: '#888' }}>Loading...</div>;
  if (error || !art) return <div style={{ textAlign: 'center', padding: '4rem', color: '#ef4444' }}>{error || 'Not found'}</div>;

  const isSale = art.status === 'for_selling';

  return (
    <>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '2rem 1rem' }}>
        {/* Breadcrumb */}
        <nav style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1.5rem' }}>
          <Link to="/marketplace" style={{ color: '#7c3aed', textDecoration: 'none' }}>Marketplace</Link>
          <span style={{ margin: '0 0.5rem' }}>›</span>
          {shop && (
            <>
              <Link to={`/marketplace/shop/${shop.artist_id}`} style={{ color: '#7c3aed', textDecoration: 'none' }}>
                {shop.shop_name || 'Artist Shop'}
              </Link>
              <span style={{ margin: '0 0.5rem' }}>›</span>
            </>
          )}
          <span style={{ color: '#ccc' }}>{art.title || 'Artwork'}</span>
        </nav>

        <div className="mp-art-detail-grid">
          {/* Image */}
          <div className="mp-art-detail-img-wrap">
            <img
              src={resolveImageUrl(art.image_url)}
              alt={art.title || 'Artwork'}
              className="mp-art-detail-img"
              onError={onImageError}
            />
          </div>

          {/* Details */}
          <div>
            <span style={{
              fontSize: '0.8rem',
              padding: '0.25rem 0.8rem',
              borderRadius: '1rem',
              background: '#10b98133',
              color: '#10b981',
              fontWeight: 600,
            }}>
              🏷️ For Sale
            </span>

            <h1 style={{ fontSize: '2rem', fontWeight: 700, marginTop: '1rem' }}>{art.title || 'Untitled'}</h1>

            {art.description && <p style={{ color: '#aaa', lineHeight: 1.6 }}>{art.description}</p>}

            <div style={{ fontSize: '2rem', fontWeight: 700, color: '#10b981', margin: '1.5rem 0' }}>
              {formatPrice(art.price)}
            </div>

            {/* Shop Info */}
            {shop && (
              <Link to={`/marketplace/shop/${shop.artist_id}`} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  background: '#1a1a2e',
                  borderRadius: '0.75rem',
                  marginBottom: '1.5rem',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: shop.avatar_url ? `url(${resolveImageUrl(shop.avatar_url)}) center/cover` : 'linear-gradient(135deg, #7c3aed, #2563eb)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: '1.1rem',
                  }}>
                    {!shop.avatar_url && (shop.shop_name || shop.artist_name || 'A')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 600 }}>{shop.shop_name || shop.artist_name || 'Artist Shop'}</div>
                    <div style={{ color: '#888', fontSize: '0.8rem' }}>View shop</div>
                  </div>
                </div>
              </Link>
            )}

            {/* CTA */}
            <Link
              to={`/marketplace/checkout/${art.art_id}?type=sale`}
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '1rem',
                borderRadius: '0.75rem',
                background: '#10b981',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.1rem',
                textDecoration: 'none',
                transition: 'opacity 0.2s',
              }}
            >
              Buy Now
            </Link>

            {/* Meta */}
            <div style={{ marginTop: '2rem', fontSize: '0.85rem', color: '#666' }}>
              {art.dimensions && <p>Dimensions: {art.dimensions}</p>}
              {art.medium && <p>Medium: {art.medium}</p>}
              {art.year && <p>Year: {art.year}</p>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
